import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { JiraList } from '../../../model/jira/jira-list';
import { Requirement } from '../../../model/jira/release-item';
import { AccessPipelineRunService } from '../../../shared/access-api/access-pipeline-run.service';
import { JiraService } from '../../../shared/jira-service/jira.service';
import { selectJiraComponentPipelineMappingForPipeline } from '../configuration/configuration.selectors';
import { selectDeliveryFixVersions } from '../deliveries/deliveries.selectors';
import { selectJiraUiBaseUrl } from '../environment/environment.selectors';
import { selectConfigurationLoadingState } from '../loading-state/loading-state.selectors';
import { selectSelectedPipelineRunForPipeline } from '../pipeline-run/pipeline-run.selectors';
import { selectPipelineById } from '../pipeline/pipeline.selectors';
import { getTraceabilityDetails } from '../shared/traceability';
import * as TestRegressionActions from './test-regression.actions';
import { selectPipelineRequirements } from './test-regression.selectors';

@Injectable()
export class TestRegressionEffects {
  private readonly action$ = inject(Actions);
  private readonly store$ = inject(Store);
  private readonly jiraService = inject(JiraService);
  private readonly accessPipelineRunService = inject(AccessPipelineRunService);

  onLoadPipelineRequirements = createEffect(() => {
    return this.action$.pipe(
      ofType(TestRegressionActions.loadPipelineRequirements),
      // Loading the requirements depends on the jira component mapping.
      // The Jira component mapping is fetched on group level, not on pipeline level.
      // We cannot use a side effect to trigger of the requirements, since we only want to do that when a pipeline is expanded.
      // Therefore we use concatMap here to make sure that the mapping is available before proceeding.
      // This is especially important for the cypress tests since expanding a pipeline happens immediately without any delay.
      concatMap((action) =>
        this.store$.select(selectConfigurationLoadingState).pipe(
          filter((loadingState) => loadingState === 'completed'),
          take(1),
          map(() => action),
        ),
      ),
      mergeMap((action) => {
        return of(action).pipe(
          withLatestFrom(
            this.store$.select(selectDeliveryFixVersions),
            this.store$.select(selectJiraComponentPipelineMappingForPipeline(action.pipelineId)),
            this.store$.select(selectPipelineById(action.pipelineId)),
            this.store$.select(selectSelectedPipelineRunForPipeline(action.pipelineId)),
          ),
        );
      }),
      mergeMap(([action, deliveryFixVersions, pipelineMapping, pipeline, selectedPipelineRunId]) => {
        const pipelineId = action.pipelineId;
        const pipelineKey = pipeline.pipelineKey;

        // get components from pipeline mapping in release config
        let components: string[] = [];
        if (pipelineMapping?.length) {
          components = pipelineMapping.filter((mapping) => mapping.pipelineKeys.includes(pipelineKey)).map((mapping) => mapping.component);
        }
        if (!deliveryFixVersions) {
          return of(TestRegressionActions.missingDeliveryFixVersionError({ pipelineId }));
        }
        return this.jiraService.getRequirementsByComponents(components, deliveryFixVersions).pipe(
          map((requirements: JiraList) => requirements.issues.map((req) => Requirement.fromJiraIssue(req))),
          mergeMap((requirements) => {
            return [
              TestRegressionActions.setPipelineRequirements({ pipelineId, requirements }),
              TestRegressionActions.loadPipelineRunRegressionTests({
                pipelineId,
                pipelineRunId: selectedPipelineRunId,
              }),
            ];
          }),
          takeUntil(
            this.action$.pipe(
              ofType(TestRegressionActions.loadPipelineRequirements),
              filter((newAction) => newAction.pipelineId === action.pipelineId),
            ),
          ),
          catchError(() => {
            return of(TestRegressionActions.loadPipelineRequirementsError({ pipelineId: pipelineId }));
          }),
        );
      }),
    );
  });

  onLoadPipelineRunRegressionTests = createEffect(() => {
    return this.action$.pipe(
      ofType(TestRegressionActions.loadPipelineRunRegressionTests),
      mergeMap((action) =>
        of(action).pipe(
          withLatestFrom(this.store$.select(selectPipelineRequirements(action.pipelineId)), this.store$.select(selectJiraUiBaseUrl)),
        ),
      ),
      mergeMap(([action, pipelineRequirements, jiraUiBaseUrl]) => {
        const pipelineRunId = action.pipelineRunId;

        if (!pipelineRequirements) {
          return of(TestRegressionActions.setPipelineRunRegressionTests({ pipelineRunId, pipelineRunRegressionTests: [] }));
        }

        const requirementKeys = pipelineRequirements.map((requirement: Requirement) => requirement.key);
        return this.accessPipelineRunService.getPipelineRunTestcases(pipelineRunId, requirementKeys).pipe(
          map((testcases) => {
            const pipelineRunRegressionTests = getTraceabilityDetails(pipelineRequirements, testcases, jiraUiBaseUrl);
            return TestRegressionActions.setPipelineRunRegressionTests({ pipelineRunId, pipelineRunRegressionTests });
          }),
          takeUntil(
            this.action$.pipe(
              ofType(TestRegressionActions.loadPipelineRunRegressionTests),
              filter((newAction) => newAction.pipelineRunId === action.pipelineRunId),
            ),
          ),
          catchError(() => {
            return of(TestRegressionActions.loadPipelineRunRegressionTestsError({ pipelineRunId }));
          }),
        );
      }),
    );
  });
}
